/*
 * Goods as icons, for the recipe picker and the map's plant badges: a
 * shape by what the good is (an ore is a chunk, a metal an ingot, fuel a
 * can) in its colour, so a good reads the same wherever it shows. Ores
 * take their ore's map colour (the heatmap's), metals their metal's.
 * Drawn on a 24-unit square as SVG markup.
 */

import { ORE_HUES, oreLabel } from "../render/colour";

/** An ore's map colour by the good's name ("Iron ore", "Magnesite"), or
 *  null when the good isn't an ore. */
function oreHue(name: string): string | null {
  const bare = name.replace(/ ore$/, "");
  return ORE_HUES.find(([o]) => oreLabel(o) === bare || o === bare)?.[1] ?? null;
}

const METALS: Record<string, string> = {
  Steel: "#9aa6b0",
  Copper: "#d98c55",
  Aluminium: "#dde3e8",
  Bronze: "#c4963f",
};

const EDGE = `stroke="#0d0d0d" stroke-width="1.2" stroke-linejoin="round"`;

const chunk = (fill: string) =>
  `<path d="M3.5 15.5 L6.5 8 L12.5 4.5 L19 7.5 L21 14.5 L16.5 20 L8 20.5 Z" fill="${fill}" ${EDGE}/>` +
  `<path d="M6.5 8 L12 11 L19 7.5 M12 11 L13 20.3" fill="none" stroke="#0d0d0d" stroke-opacity="0.45" stroke-width="1"/>`;

const ingot = (fill: string) =>
  `<path d="M3 17 L7 9 L17 9 L21 17 Z" fill="${fill}" ${EDGE}/>` +
  `<path d="M7 9 L8.5 6 L18.5 6 L17 9 Z" fill="${fill}" ${EDGE}/>` +
  `<path d="M18.5 6 L22 13.5 L21 17 L17 9 Z" fill="${fill}" fill-opacity="0.75" ${EDGE}/>` +
  `<path d="M3 17 L21 17" stroke="#ffffff" stroke-opacity="0.35" stroke-width="1"/>`;

const SHAPES: Record<string, string> = {
  // A riveted plate.
  "Armour steel":
    `<path d="M3 7 L18 4 L21 16 L6 20 Z" fill="#6c7984" ${EDGE}/>` +
    [[6.5, 8.5], [16.5, 6.5], [18.3, 14.8], [8.3, 17.2]].map(([x, y]) => `<circle cx="${x}" cy="${y}" r="1.2" fill="#c9d1d8"/>`).join(""),
  // A drill bit.
  "Tungsten carbide": `<path d="M9 2.5 L15 2.5 L15 13 L12 21.5 L9 13 Z" fill="#5b6390" ${EDGE}/><path d="M9 6 L15 8.5 M9 9.5 L15 12" stroke="#c7cbe8" stroke-width="1.1"/>`,
  // An I-beam.
  "Light alloy": `<path d="M4 4 L20 4 L20 7.5 L14 7.5 L14 16.5 L20 16.5 L20 20 L4 20 L4 16.5 L10 16.5 L10 7.5 L4 7.5 Z" fill="#a9d4ea" ${EDGE}/>`,
  Coal:
    `<path d="M3.5 15.5 L6.5 8 L12.5 4.5 L19 7.5 L21 14.5 L16.5 20 L8 20.5 Z" fill="#2b2b2b" stroke="#e8d27a" stroke-width="1.2" stroke-linejoin="round"/>` +
    `<path d="M6.5 8 L12 11 L19 7.5 M12 11 L13 20.3" fill="none" stroke="#e8d27a" stroke-opacity="0.5" stroke-width="1"/>`,
  Oil: `<path d="M12 2.5 C15 7.5 19 11 19 15 A7 7 0 0 1 5 15 C5 11 9 7.5 12 2.5 Z" fill="#241a2a" stroke="#c86ad8" stroke-width="1.4"/><path d="M8.5 15 A3.5 3.5 0 0 0 11 18.5" fill="none" stroke="#c86ad8" stroke-width="1.2" stroke-linecap="round"/>`,
  Fuel: `<path d="M5 6 L15 6 L19 10 L19 21 L5 21 Z" fill="#c4562c" ${EDGE}/><rect x="6.5" y="2.5" width="6" height="3.5" fill="#c4562c" ${EDGE}/><path d="M8 11 L16 18 M16 11 L8 18" stroke="#0d0d0d" stroke-opacity="0.5" stroke-width="1.4"/>`,
  Munitions:
    [5, 10, 15]
      .map((x) => `<path d="M${x} 21 L${x} 10 Q${x + 2} 4 ${x + 4} 10 L${x + 4} 21 Z" fill="#d8b050" ${EDGE}/><rect x="${x}" y="16" width="4" height="5" fill="#a97f2c" ${EDGE}/>`)
      .join(""),
  Recruits: `<circle cx="12" cy="7.5" r="4" fill="#e8dfcc" ${EDGE}/><path d="M4 21.5 C4 15 7.5 12.5 12 12.5 C16.5 12.5 20 15 20 21.5 Z" fill="#e8dfcc" ${EDGE}/>`,
  // A barracks: a hall with a flag; a foundry: a hall with a stack.
  Barracks: `<path d="M3 21 L3 11 L12 6 L21 11 L21 21 Z" fill="#8f877c" ${EDGE}/><rect x="9.5" y="15" width="5" height="6" fill="#2b2b2b"/><path d="M12 6 L12 1.5" stroke="#e8dfcc" stroke-width="1.2"/><path d="M12 1.5 L16 3 L12 4.5 Z" fill="#c4512f"/>`,
  Foundry: `<path d="M3 21 L3 12 L15 12 L15 21 Z" fill="#7a6f66" ${EDGE}/><rect x="16" y="5" width="4" height="16" fill="#5c534c" ${EDGE}/><path d="M6 16 L12 16" stroke="#e0a53a" stroke-width="2"/>`,
  People:
    `<circle cx="8" cy="8" r="3.2" fill="#c9bfa8" ${EDGE}/><path d="M2 20 C2 14.5 4.5 12.5 8 12.5 C11.5 12.5 14 14.5 14 20 Z" fill="#c9bfa8" ${EDGE}/>` +
    `<circle cx="16" cy="7" r="3.6" fill="#e8dfcc" ${EDGE}/><path d="M9 21.5 C9 15 12 12.3 16 12.3 C20 12.3 23 15 23 21.5 Z" fill="#e8dfcc" ${EDGE}/>`,
  Batteries: `<rect x="5" y="5.5" width="14" height="16" rx="1.5" fill="#3f9a62" ${EDGE}/><rect x="9" y="2.5" width="6" height="3" fill="#cfcfcf" ${EDGE}/><path d="M12 10 L12 17 M8.5 13.5 L15.5 13.5" stroke="#f0f0f0" stroke-width="2"/>`,
  Magnets: `<path d="M4 4 L9 4 L9 13 A3 3 0 0 0 15 13 L15 4 L20 4 L20 13 A8 8 0 0 1 4 13 Z" fill="#cc4141" ${EDGE}/><rect x="4" y="4" width="5" height="4" fill="#d6d6d6" ${EDGE}/><rect x="15" y="4" width="5" height="4" fill="#d6d6d6" ${EDGE}/>`,
  Explosives:
    [4.5, 9.5, 14.5].map((x) => `<rect x="${x}" y="9" width="5" height="12.5" rx="1" fill="#c23a32" ${EDGE}/>`).join("") +
    `<path d="M12 9 C12 6 14 5 16 3.5" fill="none" stroke="#e8dfcc" stroke-width="1.3"/><circle cx="16.5" cy="3" r="1.4" fill="#ffd34d"/><rect x="4" y="13" width="16" height="2.5" fill="#2b2b2b"/>`,
  Train: `<path d="M3 17 L3 10 L10 10 L10 5 L17 5 L17 10 L21 13 L21 17 Z" fill="#b9bec4" ${EDGE}/><rect x="11.5" y="6.5" width="4" height="3" fill="#2b2b2b"/><circle cx="7" cy="19" r="2.3" fill="#2b2b2b" ${EDGE}/><circle cx="16" cy="19" r="2.3" fill="#2b2b2b" ${EDGE}/><rect x="5" y="6" width="2.5" height="4" fill="#b9bec4" ${EDGE}/>`,
  Credits: `<circle cx="12" cy="12" r="9" fill="#e2c255" ${EDGE}/><circle cx="12" cy="12" r="6.3" fill="none" stroke="#9c7e22" stroke-width="1.2"/><path d="M14.6 9.6 A3.4 3.4 0 1 0 14.6 14.4" fill="none" stroke="#6b5412" stroke-width="2" stroke-linecap="round"/>`,
};

/** A good's icon as SVG markup on a 24-unit square (no <svg> around it). */
export function goodShape(name: string): string {
  if (name === "Rounds") name = "Munitions";
  if (SHAPES[name]) return SHAPES[name];
  if (METALS[name]) return ingot(METALS[name]);
  const ore = oreHue(name);
  if (ore) return chunk(ore);
  return `<circle cx="12" cy="12" r="8" fill="#8a8a8a" ${EDGE}/>`;
}

/** A good's icon as a whole <svg>, `size` px square. */
export function goodSvg(name: string, size = 24): string {
  return `<svg class="good-icon" width="${size}" height="${size}" viewBox="0 0 24 24" aria-hidden="true">${goodShape(name)}</svg>`;
}

/** What a recipe's tile shows: its first output, or what it does when it
 *  makes no good (a train, credits). */
export function recipeIconGood(r: { name: string; outputs: [number, string, number][] }): string {
  if (r.outputs.length) return r.outputs[0][1];
  if (/locomotive/i.test(r.name)) return "Train";
  if (/mint/i.test(r.name)) return "Credits";
  return r.name;
}

/** "Not linked by rail" (Factorio's disconnected-wire mark): a broken
 *  rail in red, `size` px. */
export const UNLINKED_SVG = (size: number) =>
  `<svg class="good-icon" width="${size}" height="${size}" viewBox="0 0 24 24" aria-hidden="true">` +
  `<path d="M2 9 L9 9 M2 15 L9 15 M15 9 L22 9 M15 15 L22 15" stroke="#e0624a" stroke-width="2.4" stroke-linecap="round"/>` +
  `<path d="M4 7 L4 17 M7.5 7 L7.5 17 M16.5 7 L16.5 17 M20 7 L20 17" stroke="#e0624a" stroke-width="1.6"/>` +
  `<path d="M10.5 6 L13.5 18" stroke="#f2efe8" stroke-width="2" stroke-linecap="round"/></svg>`;
